import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import {
  Box, Button, TextField, Avatar, Typography,
  List, ListItemButton, ListItemAvatar, ListItemText,
  Chip, ToggleButton, ToggleButtonGroup, CircularProgress,
} from '@mui/material';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';
import { ScannedItem } from './BarcodeMealScanner';

interface FoodProduct {
  name: string;
  brand: string;
  kcal_per_100g: number;
  protein_per_100g: number;
  carbs_per_100g: number;
  fat_per_100g: number;
  image_url: string;
  serving_size: string;
  barcode: string;
}

interface Props {
  onDone: (items: ScannedItem[]) => void;
  onCancel: () => void;
}

type Unit = 'g' | 'ml' | 'cl';
const unitMultiplier: Record<Unit, number> = { g: 1, ml: 1, cl: 10 };

function isLiquid(product: FoodProduct): boolean {
  return /\b(m?l|cl|dl)\b/i.test(product.serving_size ?? '');
}

function computeNutrition(product: FoodProduct, amount: number, unit: Unit) {
  const factor = (amount * unitMultiplier[unit]) / 100;
  return {
    calories: Math.round(factor * product.kcal_per_100g),
    protein_g: Math.round(factor * product.protein_per_100g * 10) / 10,
    carbs_g: Math.round(factor * product.carbs_per_100g * 10) / 10,
    fat_g: Math.round(factor * product.fat_per_100g * 10) / 10,
  };
}

type View = 'searching' | 'confirming';

const FoodSearchMealAdder: React.FC<Props> = ({ onDone, onCancel }) => {
  const { t } = useLanguage();
  const [view, setView] = useState<View>('searching');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoodProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [pending, setPending] = useState<FoodProduct | null>(null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<Unit>('g');
  const [items, setItems] = useState<ScannedItem[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setSearched(true);
      try {
        const res = await api.get('/food/search', { params: { q }, signal: controller.signal });
        setResults(res.data.products ?? []);
      } catch (err: unknown) {
        const name = (err as { name?: string })?.name;
        if (name !== 'CanceledError' && name !== 'AbortError') {
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  const handleSelect = (product: FoodProduct) => {
    setPending(product);
    setAmount('');
    setUnit(isLiquid(product) ? 'ml' : 'g');
    setView('confirming');
  };

  const buildItem = (): ScannedItem | null => {
    if (!pending) return null;
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) return null;
    const nutrition = computeNutrition(pending, amountNum, unit);
    return { product: pending, amount: amountNum, unit, ...nutrition };
  };

  const addItem = (): ScannedItem[] => {
    const item = buildItem();
    if (!item) return items;
    const updated = [...items, item];
    setItems(updated);
    return updated;
  };

  const handleAddAndSearchNext = () => {
    addItem();
    setQuery('');
    setResults([]);
    setSearched(false);
    setView('searching');
  };

  const handleAddAndDone = () => {
    const updated = addItem();
    if (updated.length > 0) onDone(updated);
  };

  const handleDeleteItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const parsedAmount = parseFloat(amount);
  const previewCalories =
    pending && parsedAmount > 0
      ? computeNutrition(pending, parsedAmount, unit).calories
      : null;

  if (view === 'searching') {
    return (
      <Box>
        {items.length > 0 && (
          <Button
            variant="contained"
            fullWidth
            sx={{ mb: 1 }}
            onClick={() => onDone(items)}
          >
            {t.doneScanItems.replace('{n}', String(items.length))}
          </Button>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder={t.searchFoodProducts}
          value={query}
          onChange={handleQueryChange}
          sx={{ mb: 1 }}
          autoFocus
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {searched && !loading && results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
            {t.noProductsFound}
          </Typography>
        )}

        {results.length > 0 && (
          <List dense sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {results.map((product, idx) => (
              <ListItemButton key={idx} onClick={() => handleSelect(product)}>
                <ListItemAvatar>
                  <Avatar src={product.image_url} alt={product.name} variant="rounded" sx={{ width: 40, height: 40 }} />
                </ListItemAvatar>
                <ListItemText
                  primary={product.name}
                  secondary={product.brand}
                />
                <Chip label={`${product.kcal_per_100g} kcal`} size="small" />
              </ListItemButton>
            ))}
          </List>
        )}

        {items.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {items.map((item, idx) => (
              <Chip
                key={idx}
                label={`${item.product.name} · ${item.amount}${item.unit} · ${item.calories} kcal`}
                size="small"
                onDelete={() => handleDeleteItem(idx)}
              />
            ))}
          </Box>
        )}

        <Button variant="outlined" size="small" fullWidth sx={{ mt: 2 }} onClick={onCancel}>
          {t.cancel}
        </Button>
      </Box>
    );
  }

  // confirming view
  return (
    <Box>
      {pending && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Avatar
            src={pending.image_url}
            alt={pending.name}
            variant="rounded"
            sx={{ width: 56, height: 56 }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
              {pending.name}
            </Typography>
            {pending.brand && (
              <Typography variant="body2" color="text.secondary">
                {pending.brand}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {pending.kcal_per_100g} kcal / 100{unit === 'g' ? 'g' : unit}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TextField
          label={t.amount}
          type="number"
          size="small"
          value={amount}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: 'any' }}
          sx={{ width: 110 }}
          autoFocus
        />
        <ToggleButtonGroup
          value={unit}
          exclusive
          size="small"
          onChange={(_e, val) => { if (val) setUnit(val as Unit); }}
        >
          <ToggleButton value="g">g</ToggleButton>
          <ToggleButton value="ml">ml</ToggleButton>
          <ToggleButton value="cl">cl</ToggleButton>
        </ToggleButtonGroup>
        {previewCalories !== null && (
          <Typography variant="body2" color="text.secondary">
            ≈ {previewCalories} kcal
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAddAndSearchNext}
          disabled={!parsedAmount || parsedAmount <= 0}
        >
          {t.addAndSearchNext}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleAddAndDone}
          disabled={!parsedAmount || parsedAmount <= 0}
        >
          {t.addAndDone}
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={() => setView('searching')}
        >
          {t.searchFood}
        </Button>
      </Box>

      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {items.map((item, idx) => (
            <Chip
              key={idx}
              label={`${item.product.name} · ${item.amount}${item.unit} · ${item.calories} kcal`}
              size="small"
              onDelete={() => handleDeleteItem(idx)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FoodSearchMealAdder;
